import * as React from 'react';

import {
  DataGrid,
  ListScreenLayout,
  ListStatsCardProps,
  Pagination,
  SearchField,
  Select,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';
import {
  LucideClock,
  LucideLoaderCircle,
  LucideCheckCircle,
} from 'lucide-react';

import { SortingQueueFlattenedItem } from '../../_graphql/querySortingQueue';
import { $remoteFacilities } from '../../_state/facilities.state';
import * as state from '../../_state/sortingQueue.state';
import { SortingStatusFilter } from '../../_state/sortingQueue.state';
import { useTranslations } from '../../i18n/useTranslations';
import { tradeRoutePaths as tradeRoutes } from '../../routes';

import { useSortingQueueColumns } from './hooks/useSortingQueueColumns';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

//
// ⚛️ ⚛️ ⚛️ SORTING QUEUE LIST VIEW ⚛️ ⚛️ ⚛️
//
export const SortingQueueView = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  // Atoms
  const [selectedFacility, setSelectedFacility] = useAtom(
    state.$selectedFacility,
  );
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [statusFilter, setStatusFilter] = useAtom(state.$sortingStatusFilter);
  const [searchTerm, setSearchTerm] = React.useState<string>('');

  const { loadingState, isFetching, error } = useAtomValue(
    state.$remoteSortingQueue,
  );
  const items = useAtomValue(state.$filteredSortingQueueItems);
  const counts = useAtomValue(state.$sortingQueueCounts);
  const { data: facilitiesData } = useAtomValue($remoteFacilities);

  // Memoized values
  const facilityOptions = React.useMemo(() => {
    const facilities = facilitiesData?.facilities ?? [];
    return [
      { key: 'all', label: t.sortingQueue.facilityFilter.all },
      ...facilities.map((facility) => ({
        key: facility.id,
        label: facility.facilityName,
      })),
    ];
  }, [facilitiesData, t]);

  const statusOptions = React.useMemo(
    () => [
      { key: 'all', label: t.sortingQueue.statusFilter.all },
      { key: 'pending', label: t.sortingQueue.statusFilter.pending },
      { key: 'inProgress', label: t.sortingQueue.statusFilter.inProgress },
      { key: 'completed', label: t.sortingQueue.statusFilter.completed },
    ],
    [t],
  );

  const filteredItems = React.useMemo(() => {
    if (!searchTerm) return items;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return items.filter(
      (item) =>
        item.itemName?.toLowerCase().includes(lowerSearchTerm) ||
        item.supplierName?.toLowerCase().includes(lowerSearchTerm) ||
        item.purchaseLoadDocumentNumber.toString().includes(lowerSearchTerm) ||
        item.sortingWorkOrderNumber?.toLowerCase().includes(lowerSearchTerm),
    );
  }, [items, searchTerm]);

  const emptyPanelProps = {
    title: t.sortingQueue.emptyState.title,
    description: t.sortingQueue.emptyState.description,
    centered: true,
  };

  const statsColumns = React.useMemo(
    (): ListStatsCardProps['stats'] => [
      {
        key: 'pending',
        label: t.sortingQueue.counts.pending,
        value: counts.pending,
        icon: <LucideClock />,
        color: 'warning',
      },
      {
        key: 'inProgress',
        label: t.sortingQueue.counts.inProgress,
        value: counts.inProgress,
        icon: <LucideLoaderCircle />,
        color: 'teal',
      },
      {
        key: 'completed',
        label: t.sortingQueue.counts.completed,
        value: counts.completed,
        icon: <LucideCheckCircle />,
        color: 'success',
      },
    ],
    [counts, t],
  );

  // Callbacks
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  const handleFacilityChange = React.useCallback(
    (value: string | undefined) => {
      setSelectedFacility(value === 'all' ? undefined : value);
    },
    [setSelectedFacility],
  );

  const handleStatusChange = React.useCallback(
    (value: string | undefined) => {
      setStatusFilter((value ?? 'all') as SortingStatusFilter);
    },
    [setStatusFilter],
  );

  const handlePurchaseLoadClick = React.useCallback(
    (item: SortingQueueFlattenedItem) => {
      navigate(tradeRoutes.scaleTicketDetail, {
        params: { id: item.purchaseLoadId },
        keepSearchParams: false,
      });
    },
    [navigate],
  );

  // Other data hooks
  const columns = useSortingQueueColumns({
    onPurchaseLoadClick: handlePurchaseLoadClick,
  });

  return (
    <ListScreenLayout
      filters={
        <>
          <SearchField
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t.sortingQueue.search.placeholder}
            aria-label="Search sorting queue items"
            className="min-w-[200px]"
          />

          <Select
            value={selectedFacility ?? 'all'}
            onChange={handleFacilityChange}
            aria-label="Filter by facility"
            className="min-w-[180px]"
            options={facilityOptions}
          />

          <Select
            value={statusFilter}
            onChange={handleStatusChange}
            aria-label="Filter by sorting status"
            className="min-w-[150px]"
            options={statusOptions}
          />
        </>
      }
      stats={statsColumns}
      footer={
        counts.total > limit && (
          <Pagination
            limit={limit}
            offset={offset}
            total={counts.total}
            onChange={handlePaginationChange}
          />
        )
      }
      className="@SortingQueueView"
    >
      <DataGrid<SortingQueueFlattenedItem>
        isLoading={loadingState.isInitialLoading || isFetching}
        columns={columns}
        dataSource={filteredItems}
        rowKey="lineItemId"
        aria-label="Sorting Queue"
        className="w-full text-sm"
        fullWidth
        hasError={!!error}
        errorPanelProps={
          error
            ? {
                description: t.sortingQueue.error(error.message),
                centered: true,
              }
            : undefined
        }
        emptyPanelProps={emptyPanelProps}
      />
    </ListScreenLayout>
  );
};
